import axios from "axios";

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

class ZipzyAdminApiClient {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.authKey = "zipzy_admin_auth";

    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      timeout: 10_000, // 10 seconds
      validateStatus: () => true,
    });
  }

  async verifyAdminCredentials(username, password) {
    const endpoint = "/auth/admin/verify-credentials";
    const requestBody = {
      username: username,
      password: password,
    };

    let response = null;
    try {
      response = await this.axiosInstance.post(endpoint, requestBody);
    } catch (error) {
      return {
        success: false,
        error: {
          userFriendlyMessage: "Failed to Connect to Server.",
        },
      };
    }

    const responseJson = response.data;

    if (response.status !== 200) {
      return {
        success: false,
        error: responseJson.error,
      };
    }

    return {
      success: true,
      data: responseJson.result,
    };
  }

  async login(username, password) {
    const verifyCredentialsResult = await this.verifyAdminCredentials(username, password);

    if (!verifyCredentialsResult.success) {
      return {
        success: false,
        error: {
          userFriendlyMessage: "Invalid username or password.",
        },
      };
    }

    const token = btoa(`${username}:${password}`);
    localStorage.setItem(this.authKey, token);

    return {
      success: true,
    };
  }

  getToken() {
    const token = localStorage.getItem(this.authKey);
    return token;
  }

  isLoggedIn() {
    const token = this.getToken();
    return !!token;
  }

  async logout() {
    localStorage.removeItem(this.authKey);
  }

  getAuthHeader() {
    const token = localStorage.getItem(this.authKey);
    if (!token) return {};
    return {
      Authorization: `Basic ${token}`,
    };
  }

  async addNewUser(fullName, phoneNumber) {
    const endpoint = "/auth/admin/db/users/add";
    const token = this.getToken();
    const requestHeaders = {
      Authorization: `Basic ${token}`,
    };
    const requestConfig = {
      headers: requestHeaders,
    };
    const requestBody = {
      fullName: fullName,
      phoneNumber: phoneNumber,
      localityId: "68a2f0f02334c6e80d89b03d",
    };

    let response = null;
    try {
      response = await this.axiosInstance.post(endpoint, requestBody, requestConfig);
    } catch (error) {
      return {
        success: false,
        error: {
          userFriendlyMessage: "Failed to Connect to Server.",
        },
      };
    }

    const responseJson = response.data;

    if (response.status !== 201) {
      return {
        success: false,
        error: responseJson.error,
      };
    }

    return {
      success: true,
      data: responseJson.result,
    };
  }

  async getDriverAnalytics() {
    const endpoint = "/auth/admin/analytics/drivers";
    const token = this.getToken();
    const requestHeaders = {
      Authorization: `Basic ${token}`,
    };
    const requestConfig = {
      headers: requestHeaders,
    };

    let response = null;
    try {
      response = await this.axiosInstance.get(endpoint, requestConfig);
    } catch (error) {
      return {
        success: false,
        error: {
          userFriendlyMessage: "Failed to Connect to Server.",
        },
      };
    }

    const responseJson = response.data;

    if (response.status !== 200) {
      return {
        success: false,
        error: responseJson.error,
      };
    }

    return {
      success: true,
      data: responseJson.data,
    };
  }

  async getFilterInfo() {
    const endpoint = "/auth/admin/filters";
    const token = this.getToken();
    const requestHeaders = {
      Authorization: `Basic ${token}`,
    };
    const requestConfig = {
      headers: requestHeaders,
    };

    let response = null;
    try {
      response = await this.axiosInstance.get(endpoint, requestConfig);
    } catch (error) {
      return {
        success: false,
        error: {
          userFriendlyMessage: "Failed to Connect to Server.",
        },
      };
    }

    const responseJson = response.data;

    if (response.status !== 200) {
      return {
        success: false,
        error: responseJson.error,
      };
    }

    return {
      success: true,
      data: responseJson.data,
    };
  }

  async getRidesStats({ localityIds = [], driverIds = [], startTime = null, endTime = null } = {}) {
    const endpoint = "/auth/admin/analytics/rides"; // adjust to your actual route
    const token = this.getToken();

    const requestHeaders = {
      Authorization: `Basic ${token}`,
    };

    // ----- Build query params -----
    const params = {};

    if (Array.isArray(localityIds) && localityIds.length > 0) {
      params.localityIds = localityIds.join(",");
    }

    if (Array.isArray(driverIds) && driverIds.length > 0) {
      params.driverIds = driverIds.join(",");
    }

    if (startTime instanceof Date) {
      params.startTime = startTime.toISOString();
    }

    if (endTime instanceof Date) {
      params.endTime = endTime.toISOString();
    }

    const requestConfig = {
      headers: requestHeaders,
      params: params,
    };

    let response = null;

    try {
      response = await this.axiosInstance.get(endpoint, requestConfig);
    } catch (error) {
      return {
        success: false,
        error: {
          userFriendlyMessage: "Failed to Connect to Server.",
        },
      };
    }

    const responseJson = response.data;

    if (response.status !== 200) {
      return {
        success: false,
        error: responseJson.error,
      };
    }

    return {
      success: true,
      data: responseJson.data,
    };
  }

  // startDateTime / endDateTime are ISO strings with an explicit offset, e.g. 2026-10-15T00:00:00+05:30
  async getPeriodicMetricsReport({ startDateTime, endDateTime }) {
    const endpoint = "/analytics/admin/periodic-metrics-report";
    const token = this.getToken();
    const requestHeaders = {
      Authorization: `Basic ${token}`,
    };
    const requestConfig = {
      headers: requestHeaders,
    };
    const requestBody = {
      startDateTime: startDateTime,
      endDateTime: endDateTime,
    };

    let response = null;
    try {
      response = await this.axiosInstance.post(endpoint, requestBody, requestConfig);
    } catch (error) {
      return {
        success: false,
        error: {
          userFriendlyMessage: "Failed to Connect to Server.",
        },
      };
    }

    const responseJson = response.data;

    if (response.status !== 200) {
      return {
        success: false,
        error: responseJson.error,
      };
    }

    return {
      success: true,
      data: responseJson.result,
    };
  }

  async getVehicleAssignments({ localityIds = [] } = {}) {
    const endpoint = "/admin/vehicle-assignments";
    const token = this.getToken();
    const requestHeaders = {
      Authorization: `Basic ${token}`,
    };

    const params = {};
    if (Array.isArray(localityIds) && localityIds.length > 0) {
      params.localityIds = localityIds.join(",");
    }

    const requestConfig = {
      headers: requestHeaders,
      params: params,
    };

    let response = null;
    try {
      response = await this.axiosInstance.get(endpoint, requestConfig);
    } catch (error) {
      return {
        success: false,
        error: {
          userFriendlyMessage: "Failed to Connect to Server.",
        },
      };
    }

    const responseJson = response.data;

    if (response.status !== 200) {
      return {
        success: false,
        error: responseJson.error,
      };
    }

    return {
      success: true,
      data: responseJson.data,
    };
  }

  // assignments: [{ driverId, vehicleId }], vehicleId null unassigns the driver's vehicle
  async updateVehicleAssignments(assignments) {
    const endpoint = "/admin/vehicle-assignments";
    const token = this.getToken();
    const requestHeaders = {
      Authorization: `Basic ${token}`,
    };
    const requestConfig = {
      headers: requestHeaders,
    };
    const requestBody = {
      assignments: assignments,
    };

    let response = null;
    try {
      response = await this.axiosInstance.patch(endpoint, requestBody, requestConfig);
    } catch (error) {
      return {
        success: false,
        error: {
          userFriendlyMessage: "Failed to Connect to Server.",
        },
      };
    }

    const responseJson = response.data;

    if (response.status !== 200) {
      return {
        success: false,
        error: responseJson.error,
      };
    }

    return {
      success: true,
      data: responseJson.data,
    };
  }
}

const zipzyAdminApiClient = new ZipzyAdminApiClient("https://api.zipzyride.com");
export default zipzyAdminApiClient;
export { ZipzyAdminApiClient };
